/**
 * GAPS — the segment-and-gap model's missing half (docs/design/reimagine-glance-home.md
 * "Airport / layover / hotel"). buildDayRunEntries already gives us the chain of
 * SEGMENTS (schedule/travel/timetable/hotel-phase entries, chronological,
 * dateline-safe). A GAP is the derived interval BETWEEN two consecutive dated
 * segments and answers one question: do I have enough time, and what do I do
 * with it. Promotes annotateJourneys' travel-only LayoverInfo into a first-class
 * object that covers the WHOLE day-run chain, not just flight-to-flight.
 *
 * Pure logic, no React — this is what gaps.test.ts locks down. All
 * field/collection names come from the registry by typed property access
 * (CONSTRAINTS #5 — no name literals here).
 *
 * Thresholds are KIND-AWARE (an at-risk flight connection and an at-risk hotel
 * drive are different animals) and are LOCKED by gaps.test.ts:
 *   - air↔air (flight-to-flight connection, the hardest kind of gap):
 *     tight  < 90 min   (below TripIt/Flighty's usual "tight connection" call)
 *     at_risk < 45 min   (below most airlines' minimum connection time)
 *   - everything else (ground/venue/hotel legs, or any gap touching a
 *     non-flight segment): a drive to a venue has far more slack built in —
 *     tight  < 45 min
 *     at_risk < 20 min
 * A negative slack (the next segment starts before the previous one ends —
 * double-booked or already blown) is always at_risk regardless of kind.
 */
import { registry } from "@td/registry";
import { minutesOf, type DayRunEntry } from "./dayRun.ts";

export type GapStatus = "makeable" | "tight" | "at_risk";

export interface Gap {
  /** start(next) − end(prev), venue-local, dateline-safe. Can be negative. */
  slackMinutes: number;
  status: GapStatus;
  /** Arrival place of the previous segment (where the wait begins). */
  head: string;
  /** Departure place of the next segment (where the wait ends). */
  tail: string;
  /** "{prevCollection}->{nextCollection}", e.g. "travel->travel" — the render
   * context selector (airport/layover/hotel are views of a kindPair, never
   * separate screens — see the design doc). */
  kindPair: string;
}

const FLIGHT_KIND = registry.travel.fields.kind.enum!.values[0]; // "flight" — never a literal (CONSTRAINTS #5)

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Whole days added to a YYYY-MM-DD (can be negative). Local twin of
 * dayRun.ts's wholeDayDiff, needed here to go the other direction: an
 * entry's END day is `entry.day` shifted by `entry.endDayOffset`. */
function shiftDay(day: string, offsetDays: number): string {
  const [y, mo, d] = day.split("-").map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(y, mo - 1, d + offsetDays));
  const yyyy = String(shifted.getUTCFullYear()).padStart(4, "0");
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(shifted.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** True when `entry` is a travel leg whose `kind` is the flight enum value. */
function isFlightLeg(entry: DayRunEntry): boolean {
  if (entry.collection !== registry.travel.key) return false;
  return s(entry.item.fields[registry.travel.fields.kind.key]) === FLIGHT_KIND;
}

/**
 * The place a gap touches on `entry`'s side: arrival place for the segment
 * BEFORE the gap, departure place for the segment AFTER it. Registry typed
 * access per collection — no field-name literals (CONSTRAINTS #5).
 */
function placeOf(entry: DayRunEntry, side: "arrival" | "departure"): string {
  const key = entry.collection;
  if (key === registry.travel.key) {
    const tv = registry.travel.fields;
    if (side === "arrival") {
      const place = s(entry.item.fields[tv.arr_place.key]);
      return place !== "" ? place : s(entry.item.fields[tv.arr_iata.key]).toUpperCase();
    }
    const place = s(entry.item.fields[tv.dep_place.key]);
    return place !== "" ? place : s(entry.item.fields[tv.dep_iata.key]).toUpperCase();
  }
  if (key === registry.schedule.key) {
    return s(entry.item.fields[registry.schedule.fields.place.key]);
  }
  if (key === registry.timetable.key) {
    return s(entry.item.fields[registry.timetable.fields.stage.key]);
  }
  if (key === registry.hotels.key) {
    const ho = registry.hotels.fields;
    const name = s(entry.item.fields[ho.name.key]);
    return name !== "" ? name : s(entry.item.fields[ho.address.key]);
  }
  return "";
}

/** kind-aware thresholds (see module note — locked by gaps.test.ts). */
function statusOf(slackMinutes: number, prev: DayRunEntry, next: DayRunEntry): GapStatus {
  if (slackMinutes < 0) return "at_risk";
  const airToAir = isFlightLeg(prev) && isFlightLeg(next);
  const tightBelow = airToAir ? 90 : 45;
  const atRiskBelow = airToAir ? 45 : 20;
  if (slackMinutes < atRiskBelow) return "at_risk";
  if (slackMinutes < tightBelow) return "tight";
  return "makeable";
}

/** The end instant of `entry`, as venue-local day+time (its own end time,
 * shifted onto its actual end day via endDayOffset — dateline/red-eye-safe,
 * the same discipline buildDayRunEntries already encodes on the entry). */
function endOf(entry: DayRunEntry): { day: string; time: string } {
  const day = entry.endDayOffset !== 0 ? shiftDay(entry.day, entry.endDayOffset) : entry.day;
  const time = entry.endTime !== "" ? entry.endTime : entry.time;
  return { day, time };
}

/**
 * The gap for every interval between two consecutive DATED entries of an
 * already-sorted day-run chain (buildDayRunEntries' output — pass it
 * straight through). Undated entries carry no wall-clock instant and are
 * skipped as gap bounds (CONSTRAINTS #3 still shows them elsewhere; they are
 * simply not a boundary of a timed interval). `tz` is accepted for parity
 * with the design doc's `buildGaps(entries, tz)` signature and future
 * cross-timezone rendering; all math here is venue-local wall time (ADR-006)
 * exactly like minutesOf/annotateJourneys — no tz conversion happens today.
 */
export function buildGaps(entries: readonly DayRunEntry[], _tz: string): Gap[] {
  const dated = entries.filter((e) => e.day !== "");
  const gaps: Gap[] = [];
  for (let i = 0; i < dated.length - 1; i++) {
    const prev = dated[i]!;
    const next = dated[i + 1]!;
    const end = endOf(prev);
    const endMin = minutesOf(end.day, end.time);
    const startMin = minutesOf(next.day, next.time);
    if (endMin === null || startMin === null) continue; // untimed bound — no interval to measure
    const slackMinutes = startMin - endMin;
    gaps.push({
      slackMinutes,
      status: statusOf(slackMinutes, prev, next),
      head: placeOf(prev, "arrival"),
      tail: placeOf(next, "departure"),
      // The two collection keys ARE the registry names (no literal — CONSTRAINTS #5);
      // airport/layover/hotel are render contexts of a kindPair, never screens.
      kindPair: `${prev.collection}->${next.collection}`,
    });
  }
  return gaps;
}
